/* eslint-disable prefer-destructuring */
import { makeStyles } from '@material-ui/core';
import { Avatar, Breadcrumb, Carousel, Layout } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import NoxTable from 'src/component/Table';
import { getAllCategories } from 'src/util/getAllCategories';
import { isCurrentProjectBuildy, isCurrentProjectStimull } from 'src/util/projectCheckers';
import ItemDetailEdit from './ItemDetailEdit';
import ItemOptionsEdit from './ItemOptionsEdit';
import ItemPersonalizedBody from './ItemPersonalizedBody';
import {
  cleanupAction,
  fetchCategoriesAction,
  fetchItemAction,
  setItemDraft,
  updateItemAction
} from '../../actions/ItemAction';
import ArrowNextIcon from '../../asset/arrow-next.svg';
import CButton from '../../component/Button';
import Copy from '../../component/Copy';
import InnerHeader from '../../component/InnerHeader';
import Loader from '../../component/Loader/Loader';
import { MultipleSelect } from '../../component/Select';

const useStyles = makeStyles({
  carousel: {
    gridColumn: '3',
    gridRow: '1 / span 5',
    maxWidth: '230px',
    textAlign: 'start'
  },
  description: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    maxHeight: '100px',
    overflowY: 'auto'
  },
  featuredOn: {
    alignItems: 'start',
    display: 'flex',
    flexDirection: 'row',
    transform: 'translateY(-3px)'
  },
  featuredOnText: {
    display: 'flex',
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    justifyContent: 'center',
    transform: 'translateY(5px)'
  },
  featuredOnValue: {
    display: 'flex',
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    justifyContent: 'center',
    marginLeft: '10px',
    transform: 'translateY(8px)'
  },
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '70& 30%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridContainerData: {
    display: 'grid',
    gap: '25px',
    gridTemplateColumns: '1fr 1fr'
  },
  gridGeneralData: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  gridHeader: {
    gridColumn: '1 / span 2',
    gridRow: '1',
    textAlign: 'start'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridPersonalizedData: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  input: {
    '& .MuiInputBase-root': {
      background: 'red',
      border: 'none'
    }
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  }
});

const CustomersDetail = () => {
  const { itemId } = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();
  const [edit, setEdit] = useState(false);
  const selectedItem = useSelector(({ item }) => item.selectedItem);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deletedImagesIds, setDeletedImagesIds] = useState([]);
  const [rerender, setRerender] = useState(0);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [dataDraft, setDataDraft] = useState([]);
  const currentProject = useSelector(({ project }) => project.currentProject);
  const itemImagesDraft = useSelector(({ item }) => item.itemImagesDraft);
  const itemDraft = useSelector(({ item }) => item.itemDraft);
  const categories = useSelector(({ item }) => item.categories);
  const childRef = useRef(null);

  const selectedCategory = getAllCategories(categories).filter(
    (category) => category._id === selectedItem?.categoryId
  )[0];

  const subCategories = isCurrentProjectBuildy
    ? getAllCategories(categories).filter((category) => category.index === 2)
    : getAllCategories([selectedCategory]);

  const secondLevelCategories =
    getAllCategories(categories).filter((category) => category.index === 1) || [];

  useEffect(() => {
    async function load() {
      dispatch(fetchItemAction(itemId));
      dispatch(fetchCategoriesAction());
    }

    if (currentProject) {
      load();
    }

    return dispatch(cleanupAction());
  }, [currentProject, dispatch, itemId, rerender]);

  function itemRender(route, params, routes) {
    const last = routes.indexOf(route) === routes.length - 1;

    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const routes = [
    {
      breadcrumbName: 'Items',
      path: '/ecommerce/items'
    },
    {
      breadcrumbName: selectedItem._id,
      path: `/ecommerce/items/itemId`
    }
  ];

  const names = ['Carousel / Home Page', 'Product of the Day'];

  const handleChildUpdate = () => {
    childRef.current.handleUpdate();
  };

  const onUpdate = (itemDraftState) => {
    if (!edit) {
      return;
    }

    dispatch(updateItemAction(itemId, deletedImagesIds, itemDraftState));
    setRerender((r) => r + 1);
    setEdit(false);
  };

  useEffect(() => {
    if (!selectedItem.options || !selectedItem.options.length) {
      return;
    }
    const columns = createColumns(selectedItem.options);
    setColumns(columns.length ? columns[0] : []);
    const newData = selectedItem.options.map((option) => {
      if (option?.size && option.size?.unit) {
        return { ...option, size: option.size?.unit };
      }
      return option;
    });
    setData(newData);
    setDataDraft(newData);
  }, [selectedItem, selectedItem.options]);

  const createColumns = (data) => {
    if (!data || !data.length) {
      return [];
    }

    const columns = data.map((option) => {
      delete option.feats;
      const dynamicColumns = Object.keys(option);

      const finalColumn = dynamicColumns.map((column) => {
        return {
          dataIndex: column,
          key: column,
          render: (column) => {
            return <Copy text={column} />;
          },
          sorter: (a, b) => a[column].localeCompare(b[column]),
          title: column
        };
      });
      return [...finalColumn];
    });

    return columns || [];
  };

  const ItemHeader = () => {
    const { _id, title, images } = selectedItem;

    return (
      <div
        style={{
          display: 'flex'
        }}
      >
        <Avatar
          src={images[0].url}
          style={{
            display: 'flex',
            height: '55px',
            width: '55px'
          }}
        />
        <div
          className={classes.gridContainer}
          style={{
            marginLeft: '35px',
            marginTop: '0px',
            width: '100%'
          }}
        >
          <div className={classes.gridHeader}>
            <span className={classes.span}>{title || '-'}</span>
            <p className={classes.p}>
              ID: <span style={{ color: 'black' }}>{_id || '-'}</span>
            </p>
          </div>
          <div className={classes.featuredOn}>
            <div className={classes.featuredOnText}>Featured On: </div>
            {!edit ? (
              <div className={classes.featuredOnValue}>
                {selectedItem.featuredOn?.toString() || '-'}
              </div>
            ) : (
              <MultipleSelect
                currentValue={itemDraft.featuredOn || []}
                handleChange={(value) => {
                  dispatch(
                    setItemDraft({
                      ...itemDraft,
                      featuredOn: typeof value === 'string' ? value.split(',') : value
                    })
                  );
                }}
                selectOptions={names}
                styles={{ marginLeft: '12px', width: '150px' }}
                type="text"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const ItemBody = () => {
    const { title, price, images, category, description, address, metadata } = selectedItem;
    let parentCategoryDraft;
    let secondLevelCategoryDraft;
    let selectedCategoryDraft;

    if (isCurrentProjectBuildy) {
      switch (selectedCategory?.index) {
        case 2:
          secondLevelCategoryDraft = secondLevelCategories.filter(
            (secondLevelCategory) => secondLevelCategory._id === category.parentId
          )[0];

          parentCategoryDraft = categories.filter((parentCategory) => {
            if (isCurrentProjectBuildy) {
              return parentCategory._id === secondLevelCategoryDraft?.parentId;
            }

            return parentCategory._id === category?.parentId;
          })[0];

          break;
        case 1:
          secondLevelCategoryDraft = selectedCategory;

          parentCategoryDraft = categories.filter((parentCategory) => {
            return parentCategory._id === category?.parentId;
          })[0];
          selectedCategoryDraft = {};
          break;

        case 0:
          parentCategoryDraft = selectedCategory;
          secondLevelCategoryDraft = {};
          selectedCategoryDraft = {};
          break;

        default:
          break;
      }
    } else {
      switch (selectedCategory?.index) {
        case 1:
          parentCategoryDraft = categories.filter((parentCategory) => {
            return parentCategory._id === category?.parentId;
          })[0];

          selectedCategoryDraft = selectedCategory;
          break;
        case 0:
          parentCategoryDraft = selectedCategory;
          selectedCategoryDraft = {};
          break;

        default:
          break;
      }
    }

    return !edit ? (
      <div className={classes.gridContainer}>
        <div className={classes.gridContainerData}>
          <div className={classes.gridGeneralData}>
            <div className={classes.gridItem}>
              <p className={classes.p}>Title</p>
              <span className={classes.span}>{title || '-'}</span>
            </div>

            <div className={classes.gridItem}>
              <p className={classes.p}>Price</p>
              <span className={classes.span}>
                {`${price} ${isCurrentProjectStimull ? '₾' : '$'}` || '-'}
              </span>
            </div>
            <div className={classes.gridItem}>
              <p className={classes.p}>Category</p>
              <span className={classes.span}>{parentCategoryDraft?.name}</span>
            </div>
            {isCurrentProjectBuildy && (
              <div className={classes.gridItem}>
                <p className={classes.p}>Second Level Category</p>
                <span className={classes.span}>{secondLevelCategoryDraft?.name || '-'}</span>
              </div>
            )}
            <div className={classes.gridItem}>
              <p className={classes.p}>Sub-Category</p>
              <span className={classes.span}>{selectedCategoryDraft?.name || '-'}</span>
            </div>
            <div className={classes.gridItem}>
              <p className={classes.p}>Description</p>
              <div className={classes.description}>{description || '-'}</div>
            </div>
            <div className={classes.gridItem}>
              <p className={classes.p}>Item Location</p>
              <span className={classes.span}>
                {`${address.city || '-'}, ${address.street || '-'}, ${address.apartment || '-'}`}
              </span>
            </div>
          </div>

          <div className={classes.gridPersonalizedData}>
            <ItemPersonalizedBody order={itemDraft} type={selectedItem.type} />
          </div>
        </div>

        <div className={classes.carousel}>
          <Carousel autoplay autoplaySpeed={5000}>
            {images.map((image, index) => {
              return <img key={image.url} alt={`item number ${index}`} src={image.url} />;
            })}
          </Carousel>
        </div>
      </div>
    ) : (
      <ItemDetailEdit
        ref={childRef}
        deletedImagesIds={deletedImagesIds}
        onUpdate={onUpdate}
        secondLevelCategories={secondLevelCategories}
        selectedCategory={selectedCategory}
        selectedImages={itemImagesDraft}
        selectedItem={selectedItem}
        setDeletedImagesIds={setDeletedImagesIds}
        stimullFee={metadata?.stimullFee}
        subCategories={subCategories}
      />
    );
  };

  const isLoaded = useSelector(({ item }) => item.isLoaded);

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        {!edit ? (
          <div>
            <CButton onClick={() => setEdit(true)} type="primary">
              Edit
            </CButton>
          </div>
        ) : (
          <div>
            <CButton onClick={() => setEdit(false)} type="secondary">
              Cancel
            </CButton>
            <CButton onClick={handleChildUpdate} type="primary">
              Save
            </CButton>
          </div>
        )}
      </div>
      <div style={{ marginTop: '55px', padding: '0px 40px 0px 35px' }}>
        <ItemHeader />
        <ItemBody />
      </div>
      <div
        style={{
          height: 'calc(100vh - 165px)',
          marginTop: '80px',
          overflowY: 'auto'
        }}
      >
        <InnerHeader subtitle={selectedItem.options.length} title="Options" />
        {edit ? (
          <ItemOptionsEdit
            columns={columns}
            data={dataDraft}
            searchBy="title"
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        ) : (
          <NoxTable
            checkbox={false}
            columns={columns}
            data={data}
            searchBy="title"
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        )}
      </div>
    </Layout>
  );
};

export default CustomersDetail;
